import { AppContext } from "@Context/common/general/context";
import useI18n from "@i18n";
import { Box, CardContent, CardHeader, Skeleton, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { isNumber } from "lodash";
import React, { FC, useContext, useEffect, useState } from "react";
import { PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, ResponsiveContainer } from "recharts";
import ImprovementIndicator from "./components/improvement-indicator";
import RadarChartDot from "./components/radar-chart-dot";
import RadarChartTick from "./components/radar-chart-tick";
import { ProgressCard } from "./styled-components";
import { RadarMetricInfo } from "@Helpers/types";

const Indicator = ({ name, color }: { name: string; color: string }) => {
  const theme = useTheme();
  return (
    <Stack direction={"row"} spacing={1} alignItems="center">
      <Box
        sx={{
          backgroundColor: color,
          width: theme.spacing(1),
          height: theme.spacing(1),
          borderRadius: theme.spacing(0.25),
        }}
      />
      <Typography variant="textXs500" color="dsNeutral.v400">
        {name}
      </Typography>
    </Stack>
  );
};

export interface IRadarGraph {
  title?: string;
  indicators?: Array<{
    name: string;
    color: string;
  }>;
  height?: number | string;
  data: RadarMetricInfo[];
  currentScore?: number;
  previousScore?: number;
  percentageChange?: number;
  animate?: boolean;
  actionButton?: React.ReactNode;
  loading?: boolean;
}

const RadarGraph: FC<IRadarGraph> = ({
  title,
  indicators,
  height = 230,
  data = [],
  currentScore,
  previousScore,
  percentageChange,
  animate = true,
  actionButton,
  loading = false,
}) => {
  const { state } = useContext(AppContext);
  const { i18n } = useI18n();
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));
  const isMobile = state.device.isMobile;
  const [transformedData, setTransformedData] = useState<RadarMetricInfo[]>([]);

  useEffect(() => {
    const transformedData = data.map((item) => ({
      ...item,
      name: i18n(`metric.${item.name}.name`),
    }));
    setTransformedData(transformedData);
  }, [data]);

  return (
    <ProgressCard backgroundColor={'transparent'}>
      {!loading ? (
        <>
          <CardHeader
            title={
              title && (
                <Box sx={{ paddingBottom: theme.spacing(1) }}>
                  <Typography variant={isMobile ? "textLg600" : "text1xl600"} color="dsBase.v200" textTransform={"capitalize"}>
                    {i18n(title)}
                  </Typography>
                </Box>
              )
            }
            subheader={
              <Stack spacing={2} direction="row" justifyContent={"space-between"}>
                {isNumber(currentScore) && (
                  <ImprovementIndicator
                    score={currentScore}
                    previousScore={previousScore || 0}
                    percentageChange={percentageChange}
                    fontSize={24}
                    highlightScore
                  />
                )}
                {indicators && (
                  <Stack direction={"row"} spacing={2}>
                    {indicators.map((indicator, index) => (
                      <Indicator key={index} {...indicator} />
                    ))}
                  </Stack>
                )}
              </Stack>
            }
            sx={{
              backgroundColor: 'transparent !important',
              [theme.breakpoints.up("md")]: {
                padding: theme.spacing(4),
                paddingBottom: theme.spacing(0),
              },
              paddingBottom: theme.spacing(0),
            }}
            action={actionButton}
          />
          <CardContent sx={{ padding: 0, paddingBottom: "16px !important", backgroundColor: 'transparent' }}>
            <ResponsiveContainer width="100%" height={height}>
              <RadarChart data={transformedData}>
                <PolarGrid gridType={"circle"} stroke={theme.palette.dsSurface.v500} strokeWidth={1} />
                {indicators && indicators.length > 1 && (
                  <Radar
                    isAnimationActive={false}
                    dataKey="previous"
                    fill={theme.palette.dsDestructive.v200}
                    fillOpacity={0.3}
                    stroke={theme.palette.dsDestructive.v200}
                  />
                )}
                <Radar
                  dot={<RadarChartDot />}
                  isAnimationActive={animate}
                  dataKey="current"
                  fill={theme.palette.dsPrimary.v300}
                  fillOpacity={0.35}
                  strokeWidth={2}
                  stroke={theme.palette.dsPrimary.v300}
                />
                <PolarAngleAxis
                  radius={300}
                  fontSize={16}
                  tick={<RadarChartTick color={theme.palette.dsNeutral.v400} />}
                  dataKey="name"
                />
                <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
              </RadarChart>
            </ResponsiveContainer>
          </CardContent>
        </>
      ) : (
        <Box sx={{ padding: theme.spacing(isSmUp ? 4 : 2) }}>
          <Skeleton variant="text" width={140} height={30} />
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Skeleton variant="text" width={90} height={50} />
            <Skeleton variant="text" width={120} height={30} />
          </Stack>
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: isSmUp ? 400 : 280 }}>
            <Skeleton variant="circular" width={isSmUp ? 340 : 220} height={isSmUp ? 340 : 220} />
          </Box>
        </Box>
      )}
    </ProgressCard>
  );
};

export default RadarGraph;
